import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, StatCard, StatusBadge } from "@/components/app/PageBits";
import { fmtINR, fmtDate, CYCLE_LABEL, downloadReceiptPdf, parseBlobError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, FileText, Wallet, Repeat, Pause, ArrowRightLeft, Sprout, CircleDot, Copy, StickyNote, Trash2, FileDown, Paperclip, AtSign, X, Zap, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const iconFor = (kind) => {
  switch (kind) {
    case "invoice": return FileText;
    case "payment": return Wallet;
    case "credit_applied": return Wallet;
    case "plan_change": return Repeat;
    case "pause": return Pause;
    case "transfer":
    case "plot_transferred_in":
    case "plot_transferred_out": return ArrowRightLeft;
    case "plot_created": return Sprout;
    default: return CircleDot;
  }
};

const toneFor = (ev) => {
  if (ev.kind === "payment") return { dot: "bg-primary text-primary-foreground", amt: "text-primary" };
  if (ev.kind === "credit_applied") return { dot: "bg-blue-600 text-white", amt: "text-blue-700" };
  if (ev.kind === "invoice") return { dot: "bg-accent text-accent-foreground", amt: "text-foreground" };
  if (ev.kind === "pause") return { dot: "bg-orange-500 text-white", amt: "" };
  if (ev.kind.startsWith("plot_transferred") || ev.kind === "transfer") return { dot: "bg-stone-700 text-white", amt: "" };
  return { dot: "bg-muted-foreground text-white", amt: "" };
};

export default function PlotDetail() {
  const { pid } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [mentions, setMentions] = useState([]);          // [{user_id, name}]
  const [teammates, setTeammates] = useState([]);
  const isAdmin = user?.role === "super_admin" || user?.role === "admin";
  const isSuperAdmin = user?.role === "super_admin";
  const [cycleOpen, setCycleOpen] = useState(false);
  const [cycleForm, setCycleForm] = useState({ new_plan: "regular", new_cycle: "quarterly", effective_date: "", reason: "" });
  const [cycleForce, setCycleForce] = useState(false);
  const [cycleAutoRestructure, setCycleAutoRestructure] = useState(false);
  const [cyclePreview, setCyclePreview] = useState(null);
  const [cyclePreviewLoading, setCyclePreviewLoading] = useState(false);

  // Edit/Delete payment (from timeline)
  const [payEditOpen, setPayEditOpen] = useState(false);
  const [payEditFor, setPayEditFor] = useState(null);
  const [payEditForm, setPayEditForm] = useState({ paid_at: "", method: "bank_transfer", reference_no: "", notes: "" });
  const [payEditBusy, setPayEditBusy] = useState(false);
  const [payDelOpen, setPayDelOpen] = useState(false);
  const [payDelFor, setPayDelFor] = useState(null);
  const [payDelBusy, setPayDelBusy] = useState(false);

  // Fetch backdate preview whenever the effective date changes while in force mode
  useEffect(() => {
    if (!cycleOpen || !cycleForce || !cycleForm.effective_date) { setCyclePreview(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setCyclePreviewLoading(true);
      try {
        const r = await api.get(`/plots/${pid}/backdate-preview`, { params: { effective_date: cycleForm.effective_date } });
        if (!cancelled) setCyclePreview(r.data);
      } catch (e) {
        if (!cancelled) setCyclePreview(null);
      } finally {
        if (!cancelled) setCyclePreviewLoading(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleOpen, cycleForce, cycleForm.effective_date]);

  const load = async () => {
    try { const r = await api.get(`/plots/${pid}/timeline`); setData(r.data); }
    catch (e) { setData({ error: e?.response?.data?.detail || "Failed" }); }
  };
  const loadNotes = async () => {
    if (!isAdmin) return;
    try { const r = await api.get(`/plots/${pid}/notes`); setNotes(r.data); } catch {}
  };
  const loadTeammates = async () => {
    if (!isAdmin) return;
    try { const r = await api.get("/admins/mentionable"); setTeammates(r.data); } catch {}
  };
  useEffect(() => { load(); }, [pid]);
  useEffect(() => { loadNotes(); loadTeammates(); }, [pid, isAdmin]);

  // ----- Payment edit/delete (timeline inline actions) -----
  const openPayEdit = (ev) => {
    setPayEditFor(ev);
    setPayEditForm({
      paid_at: (ev.paid_at || "").slice(0, 10) || new Date().toISOString().slice(0, 10),
      method: ev.method || "bank_transfer",
      reference_no: ev.reference_no || "",
      notes: ev.notes || "",
    });
    setPayEditOpen(true);
  };
  const submitPayEdit = async () => {
    setPayEditBusy(true);
    try {
      await api.patch(`/payments/${payEditFor.payment_id}`, payEditForm);
      toast.success("Payment updated");
      setPayEditOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setPayEditBusy(false); }
  };
  const openPayDelete = (ev) => { setPayDelFor(ev); setPayDelOpen(true); };
  const submitPayDelete = async () => {
    setPayDelBusy(true);
    try {
      const r = await api.delete(`/payments/${payDelFor.payment_id}`);
      if (r.data?.credit_warning) {
        toast.warning(r.data.credit_warning, { duration: 10000 });
      } else {
        toast.success("Payment deleted & invoice balance restored");
      }
      setPayDelOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setPayDelBusy(false); }
  };

  const toggleMention = (t) => {
    const has = mentions.find((m) => m.user_id === t.user_id);
    if (has) setMentions(mentions.filter((m) => m.user_id !== t.user_id));
    else {
      setMentions([...mentions, { user_id: t.user_id, name: t.name }]);
      // append @Name to text if not already
      const handle = "@" + t.name.split(" ")[0];
      if (!noteText.includes(handle)) setNoteText((noteText ? noteText + " " : "") + handle + " ");
    }
  };

  const addNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    setSavingNote(true);
    try {
      const r = await api.post(`/plots/${pid}/notes`, {
        text, mentioned_user_ids: mentions.map((m) => m.user_id),
      });
      // upload attachments to the created note
      if (pendingFiles.length > 0) {
        for (const f of pendingFiles) {
          const form = new FormData();
          form.append("file", f);
          try { await api.post(`/plots/${pid}/notes/${r.data.id}/attachments`, form, { headers: { "Content-Type": "multipart/form-data" } }); }
          catch (e) { toast.error(`Attachment ${f.name} failed: ${e?.response?.data?.detail || ""}`); }
        }
      }
      setNoteText(""); setMentions([]); setPendingFiles([]);
      loadNotes(); toast.success("Note added");
    }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSavingNote(false); }
  };

  const generateInvoice = async () => {
    setBusy(true);
    try { await api.post(`/plots/${pid}/generate-invoice`); await load(); }
    catch (e) {} finally { setBusy(false); }
  };

  const copyOwnerInvite = async () => {
    try {
      const r = await api.get(`/customers/${data.plot.primary_owner_id}/invite-text`);
      await navigator.clipboard.writeText(r.data.text);
      toast.success(`Invite copied for ${data.plot.primary_owner_name}`);
    } catch { toast.error("Copy failed"); }
  };

  const [stmtFrom, setStmtFrom] = useState(new Date(Date.now() - 365*86400*1000).toISOString().slice(0,10));
  const [stmtTo, setStmtTo] = useState(new Date().toISOString().slice(0,10));
  const downloadStatement = async () => {
    try {
      const r = await api.get(`/customers/${data.plot.primary_owner_id}/statement.pdf?from=${stmtFrom}&to=${stmtTo}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `statement_${data.plot.primary_owner_name.replace(/\s+/g,"_")}_${stmtFrom}_${stmtTo}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(()=>URL.revokeObjectURL(url), 4000);
      toast.success("Statement downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openAttachment = async (nid, fid, filename) => {
    try {
      const r = await api.get(`/plots/${pid}/notes/${nid}/attachments/${fid}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener");
      setTimeout(()=>URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Could not open attachment"); }
  };

  const deleteNote = async (nid) => {
    try { await api.delete(`/plots/${pid}/notes/${nid}`); loadNotes(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!data) return <div className="text-muted-foreground">Loading…</div>;
  if (data.error) return <div className="text-destructive">{data.error}</div>;

  const p = data.plot;
  return (
    <div>
      <div className="mb-2">
        <Link to="/plots" data-testid="back-to-plots" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3 w-3"/> Back to plots
        </Link>
      </div>
      <PageHeader
        title={`Plot ${p.plot_no}`}
        subtitle={`${p.project_name} · ${p.primary_owner_name} · every payment, credit and event captured on one timeline.`}
        actions={
          <div className="flex gap-2">
            <Button data-testid="pd-copy-invite" variant="outline" onClick={copyOwnerInvite}>
              <Copy className="h-4 w-4 mr-2"/> Copy owner invite
            </Button>
            {isSuperAdmin && (
              <Button data-testid="pd-change-cycle" variant="outline" onClick={() => {
                setCycleForm({ new_plan: p.maintenance_plan, new_cycle: p.billing_cycle, effective_date: p.next_cycle_start, reason: "" });
                setCycleForce(false);
                setCycleAutoRestructure(false);
                setCyclePreview(null);
                setCycleOpen(true);
              }}>
                <Zap className="h-4 w-4 mr-2"/> Change plan/cycle
              </Button>
            )}
            <Button data-testid="pd-gen-invoice" onClick={generateInvoice} disabled={busy || p.status !== "active"}>
              <FileText className="h-4 w-4 mr-2"/> Generate next invoice
            </Button>
          </div>
        }
      />

      <Dialog open={cycleOpen} onOpenChange={setCycleOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change plan / cycle (direct)</DialogTitle></DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-xs text-muted-foreground">
              Super admin direct change — no approval loop. Default effective date is the plot's next cycle start (<span className="font-medium">{fmtDate(p.next_cycle_start)}</span>). To <b>backdate</b> (e.g. customer only paid half-year of a yearly cycle and wants to switch now), tick the box below and pick an earlier date — then use the Invoice ⋯ More menu to Edit/Discount/Void any existing invoice that overlaps.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>New plan</Label>
                <Select value={cycleForm.new_plan} onValueChange={(v) => setCycleForm({ ...cycleForm, new_plan: v })}>
                  <SelectTrigger data-testid="pd-cycle-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="self_managed">Self-Managed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>New cycle</Label>
                <Select value={cycleForm.new_cycle} onValueChange={(v) => setCycleForm({ ...cycleForm, new_cycle: v })}>
                  <SelectTrigger data-testid="pd-cycle-cycle"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="half_yearly">Half-Yearly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Effective date</Label>
              <Input data-testid="pd-cycle-date" type="date" value={cycleForm.effective_date}
                     onChange={(e) => setCycleForm({ ...cycleForm, effective_date: e.target.value })}
                     min={cycleForce ? undefined : p.next_cycle_start} />
            </div>
            <label className="flex items-start gap-2 text-xs cursor-pointer select-none p-2 rounded border border-amber-300 bg-amber-50">
              <input type="checkbox" data-testid="pd-cycle-force" className="mt-0.5"
                     checked={cycleForce} onChange={(e) => setCycleForce(e.target.checked)} />
              <span className="text-amber-900">
                <b>Allow backdating.</b> Needed when the new effective date is before <span className="tabular font-medium">{fmtDate(p.next_cycle_start)}</span>. Existing invoices overlapping the backdated window won't change automatically — fix them individually from Invoices → ⋯ More.
              </span>
            </label>

            {cycleForce && cyclePreview && cyclePreview.is_backdate && cyclePreview.overlapping.length > 0 && (
              <div className="rounded border border-purple-300 bg-purple-50/60 p-3 space-y-2" data-testid="pd-cycle-restructure-panel">
                <div className="text-xs font-semibold text-purple-900 flex items-center gap-1.5">
                  <Zap className="h-3 w-3"/> {cyclePreview.overlapping.length} invoice{cyclePreview.overlapping.length > 1 ? "s" : ""} will be affected
                </div>
                <div className="space-y-1.5">
                  {cyclePreview.overlapping.map((o) => (
                    <div key={o.id} className="text-xs border border-purple-200 rounded bg-white/80 p-2" data-testid={`pd-cycle-restructure-row-${o.id}`}>
                      <div className="font-medium tabular">{o.invoice_no}</div>
                      <div className="text-muted-foreground">
                        {fmtDate(o.cycle_start)} → {fmtDate(o.cycle_end)} ({o.original_days}d) · amount <span className="tabular">{fmtINR(o.amount)}</span> · paid <span className="tabular text-primary">{fmtINR(o.amount_paid)}</span>
                      </div>
                      {o.blocked_reason ? (
                        <div className="mt-1 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-1.5 py-0.5">
                          Skipped: {o.blocked_reason}
                        </div>
                      ) : (
                        <div className="mt-1 text-[11px] text-purple-900">
                          → new cycle end <span className="tabular font-medium">{fmtDate(o.new_cycle_end)}</span> ({o.new_days}d), amount <span className="tabular font-medium">{fmtINR(o.new_amount)}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <label className="flex items-start gap-2 text-xs cursor-pointer select-none pt-1">
                  <input type="checkbox" data-testid="pd-cycle-auto-restructure" className="mt-0.5"
                         checked={cycleAutoRestructure}
                         onChange={(e) => setCycleAutoRestructure(e.target.checked)} />
                  <span className="text-purple-900">
                    <b>Also auto-restructure these invoices.</b> Prorates amount by days, moves cycle end to the day before effective date, recomputes balance & status. Each edit is audit-logged with "auto_restructured_from_cycle_change".
                  </span>
                </label>
              </div>
            )}
            {cycleForce && cyclePreviewLoading && (
              <div className="text-xs text-muted-foreground" data-testid="pd-cycle-preview-loading">Checking which invoices overlap…</div>
            )}
            {cycleForce && cyclePreview && cyclePreview.is_backdate && cyclePreview.overlapping.length === 0 && (
              <div className="text-xs text-muted-foreground" data-testid="pd-cycle-preview-empty">No live invoices overlap this backdate — nothing to restructure.</div>
            )}

            <div><Label>Reason {cycleForce ? "(required when backdating)" : "(optional)"}</Label><Input data-testid="pd-cycle-reason" value={cycleForm.reason} onChange={(e) => setCycleForm({ ...cycleForm, reason: e.target.value })} placeholder={cycleForce ? "e.g. customer only paid half-yearly of yearly invoice" : ""}/></div>
          </div>
          <DialogFooter>
            <Button data-testid="pd-cycle-save" onClick={async () => {
              if (cycleForce && !(cycleForm.reason || "").trim()) {
                toast.error("A reason is required when backdating");
                return;
              }
              try {
                const r = await api.post(`/plots/${pid}/change-plan-cycle`, {
                  plot_id: pid, ...cycleForm,
                  force: cycleForce,
                  auto_restructure: cycleForce && cycleAutoRestructure,
                });
                const rs = (r.data && r.data.restructured) || [];
                const sk = (r.data && r.data.skipped) || [];
                if (rs.length || sk.length) {
                  toast.success(`Cycle updated. Restructured ${rs.length} invoice${rs.length !== 1 ? "s" : ""}${sk.length ? `, skipped ${sk.length}` : ""}.`);
                } else {
                  toast.success(cycleForce ? "Backdated change applied — fix any overlapping invoices manually" : "Plan/cycle updated; courtesy email sent to owner");
                }
                setCycleOpen(false); load();
              } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
            }}>Apply change</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-md:gap-3 mb-8">
        <StatCard testId="pd-billed" label="Total billed" value={fmtINR(data.summary.billed)} />
        <StatCard testId="pd-paid" label="Total paid" value={fmtINR(data.summary.paid)} tone="good" />
        <StatCard testId="pd-outstanding" label="Outstanding" value={fmtINR(data.summary.outstanding)} tone="bad" />
        <StatCard testId="pd-invoices" label="Invoices" value={data.summary.invoice_count} hint={`${data.events.length} events`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <div className="border border-border rounded-md bg-card p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-heading mb-3">Plot</div>
          <dl className="grid grid-cols-2 gap-y-3 text-sm">
            <dt className="text-muted-foreground">Area</dt><dd className="text-right tabular font-medium">{p.area_sqft.toLocaleString("en-IN")} sqft</dd>
            <dt className="text-muted-foreground">Plan</dt><dd className="text-right capitalize font-medium">{p.maintenance_plan.replace("_"," ")}</dd>
            <dt className="text-muted-foreground">Cycle</dt><dd className="text-right font-medium">{CYCLE_LABEL[p.billing_cycle]}</dd>
            <dt className="text-muted-foreground">Start date</dt><dd className="text-right tabular">{fmtDate(p.start_date)}</dd>
            <dt className="text-muted-foreground">Next cycle</dt><dd className="text-right tabular">{fmtDate(p.next_cycle_start)}</dd>
            <dt className="text-muted-foreground">Status</dt><dd className="text-right"><StatusBadge status={p.status}/></dd>
          </dl>
        </div>
        <div className="border border-border rounded-md bg-card p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-heading mb-3">Owner</div>
          <div className="text-sm space-y-2">
            <div className="font-medium">{p.primary_owner_name}</div>
            <div className="text-muted-foreground text-xs">Primary co-owner</div>
            {p.co_owners?.length > 0 && (
              <div className="pt-3 border-t border-border space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Co-owners</div>
                {p.co_owners.map((co, i) => (
                  <div key={i} data-testid={`plot-coowner-${i}`} className="text-xs space-y-0.5">
                    <div className="flex justify-between items-start gap-2">
                      <span className="font-medium">{co.name}</span>
                      <span className="tabular text-muted-foreground shrink-0">{co.pan_masked}</span>
                    </div>
                    {(co.phone || co.email) && (
                      <div className="text-muted-foreground tabular">
                        {co.phone || ""}{co.phone && co.email ? " · " : ""}{co.email || ""}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="border border-border rounded-md bg-card p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-heading mb-3">Pause info</div>
          {p.pause_info ? (
            <div className="text-sm space-y-1">
              <div><span className="text-muted-foreground">From: </span><span className="tabular">{fmtDate(p.pause_info.start)}</span></div>
              <div><span className="text-muted-foreground">To: </span><span className="tabular">{fmtDate(p.pause_info.end)}</span></div>
              <div className="text-xs text-muted-foreground mt-2">{p.pause_info.reason || "-"}</div>
            </div>
          ) : <div className="text-sm text-muted-foreground">Currently active. No pause in effect.</div>}
        </div>
      </div>

      {isAdmin && (
        <div className="mb-10 border border-border rounded-md bg-card p-5">
          <div className="flex items-center justify-between mb-4 max-md:flex-wrap max-md:gap-3">
            <div>
              <div className="flex items-center gap-2"><StickyNote className="h-4 w-4 text-muted-foreground"/><h2 className="font-heading text-lg font-bold tracking-tight">Admin notes</h2></div>
              <p className="text-xs text-muted-foreground mt-1">Internal notes visible only to admins. Customers cannot see this.</p>
            </div>
            <div className="flex items-end gap-2 max-md:flex-wrap max-md:w-full">
              <div className="max-md:flex-1"><Label className="text-xs">From</Label><Input data-testid="pd-stmt-from" type="date" value={stmtFrom} onChange={(e)=>setStmtFrom(e.target.value)} className="h-9 w-36 max-md:w-full"/></div>
              <div className="max-md:flex-1"><Label className="text-xs">To</Label><Input data-testid="pd-stmt-to" type="date" value={stmtTo} onChange={(e)=>setStmtTo(e.target.value)} className="h-9 w-36 max-md:w-full"/></div>
              <Button data-testid="pd-stmt-download" variant="outline" onClick={downloadStatement} className="h-9 max-md:w-full"><FileDown className="h-4 w-4 mr-2"/>Owner statement</Button>
            </div>
          </div>
          <div className="mb-3">
            <Textarea data-testid="note-text" value={noteText} onChange={(e)=>setNoteText(e.target.value)} placeholder="Site visit outcome, verbal agreement, follow-up reminder…" rows={2} className="resize-y"/>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button data-testid="mention-btn" variant="outline" size="sm"><AtSign className="h-3.5 w-3.5 mr-1"/>Mention teammate</Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <div className="p-2 max-h-64 overflow-auto">
                  {teammates.length === 0 && <div className="p-2 text-xs text-muted-foreground">No teammates to mention.</div>}
                  {teammates.map((t) => {
                    const on = !!mentions.find((m) => m.user_id === t.user_id);
                    return (
                      <button key={t.user_id} data-testid={`mention-toggle-${t.user_id}`}
                              type="button"
                              onClick={()=>toggleMention(t)}
                              className={`w-full text-left px-3 py-2 rounded-md text-sm hover:bg-muted transition-colors flex items-center justify-between ${on ? "bg-muted" : ""}`}>
                        <div>
                          <div className="font-medium">{t.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.role.replace("_", " ")}</div>
                        </div>
                        {on && <span className="text-primary text-xs">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            <label className="inline-flex items-center gap-1 text-sm cursor-pointer border border-border rounded-md px-3 py-1.5 hover:bg-muted transition-colors" data-testid="attach-btn">
              <Paperclip className="h-3.5 w-3.5"/> Attach
              <input type="file" multiple accept="image/*,application/pdf" className="hidden" data-testid="attach-input"
                onChange={(e)=>setPendingFiles([...pendingFiles, ...Array.from(e.target.files || [])])}/>
            </label>
            {mentions.map((m) => (
              <span key={m.user_id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-accent/10 text-accent border border-accent/20">
                @{m.name.split(" ")[0]}
                <button onClick={()=>toggleMention(m)} className="hover:text-destructive"><X className="h-3 w-3"/></button>
              </span>
            ))}
            {pendingFiles.map((f, i) => (
              <span key={i} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-muted border border-border">
                <Paperclip className="h-3 w-3"/>{f.name} ({(f.size/1024).toFixed(0)}KB)
                <button onClick={()=>setPendingFiles(pendingFiles.filter((_, idx) => idx !== i))} className="hover:text-destructive"><X className="h-3 w-3"/></button>
              </span>
            ))}
            <div className="ml-auto"><Button data-testid="note-add" onClick={addNote} disabled={savingNote || !noteText.trim()}>Add note</Button></div>
          </div>
          <div className="space-y-3 max-h-80 overflow-auto">
            {notes.length === 0 && <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded-md">No notes yet. Add the first observation about this plot.</div>}
            {notes.map((n) => (
              <div key={n.id} data-testid={`note-row-${n.id}`} className="border border-border rounded-md p-3 bg-background">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm whitespace-pre-wrap flex-1">{n.text}</div>
                  <Button data-testid={`note-del-${n.id}`} size="sm" variant="ghost" onClick={()=>deleteNote(n.id)} title="Delete"><Trash2 className="h-3.5 w-3.5 text-destructive"/></Button>
                </div>
                {(n.mentioned_names?.length > 0 || n.attachments?.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {n.mentioned_names?.map((mn, i) => (
                      <span key={`m${i}`} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-accent/10 text-accent border border-accent/20"><AtSign className="h-2.5 w-2.5"/>{mn.split(" ")[0]}</span>
                    ))}
                    {n.attachments?.map((a) => (
                      <button key={a.file_id} onClick={()=>openAttachment(n.id, a.file_id, a.filename)} data-testid={`att-open-${a.file_id}`}
                              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted border border-border hover:bg-muted-foreground/10 transition-colors">
                        <Paperclip className="h-2.5 w-2.5"/>{a.filename}
                      </button>
                    ))}
                  </div>
                )}
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">{n.author_name} · {fmtDate(n.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-3 flex items-center justify-between">
        <h2 className="font-heading text-xl font-bold tracking-tight">Timeline</h2>
        <div className="text-xs text-muted-foreground">Newest first · {data.events.length} events</div>
      </div>
      <div className="relative border-l-2 border-border ml-4">
        {data.events.map((ev, idx) => {
          const Icon = iconFor(ev.kind);
          const tone = toneFor(ev);
          return (
            <div key={idx} data-testid={`timeline-${ev.kind}-${idx}`} className="relative pl-8 pb-6">
              <div className={`absolute -left-[13px] top-1 h-6 w-6 rounded-full grid place-items-center ${tone.dot}`}>
                <Icon className="h-3 w-3" strokeWidth={2}/>
              </div>
              <div className="border border-border rounded-md bg-card p-4 hover:-translate-y-0.5 hover:shadow-sm transition-transform">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{ev.title}</div>
                    <div className="text-xs text-muted-foreground mt-1 break-words">{ev.detail}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 tabular">{fmtDate(ev.at)}</div>
                    {ev.kind === "payment" && ev.updated_at && (
                      <div data-testid={`timeline-pay-edited-${ev.payment_id}`}
                        className="mt-2 inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-900"
                        title={`Edited by ${ev.updated_by_name || "someone"} on ${fmtDate(ev.updated_at)}`}>
                        <Pencil className="h-2.5 w-2.5" strokeWidth={2}/>
                        edited by {ev.updated_by_name || "someone"} · {fmtDate(ev.updated_at)}
                      </div>
                    )}
                  </div>
                  {ev.amount !== null && ev.amount !== undefined && (
                    <div className={`text-right tabular font-heading text-base font-bold max-md:whitespace-nowrap ${tone.amt} ${ev.amount < 0 ? "text-primary" : ""}`}>
                      {ev.amount < 0 ? "-" : ""}{fmtINR(Math.abs(ev.amount))}
                    </div>
                  )}
                  {ev.status && <StatusBadge status={ev.status}/>}
                </div>
                {ev.kind === "payment" && ev.payment_id && (
                  <div className="mt-3 flex justify-end gap-1.5 max-md:flex-wrap">
                    <Button data-testid={`timeline-receipt-${ev.payment_id}`} size="sm" variant="outline"
                            onClick={async ()=>{
                              try { await downloadReceiptPdf({ id: ev.payment_id, invoice_no: ev.invoice_no }); }
                              catch(e) { toast.error(await parseBlobError(e)); }
                            }}>
                      <FileDown className="h-3.5 w-3.5 mr-1.5"/>Download receipt
                    </Button>
                    {isAdmin && (
                      <>
                        <Button data-testid={`timeline-pay-edit-${ev.payment_id}`} size="sm" variant="outline"
                                onClick={()=>openPayEdit(ev)}>
                          <Pencil className="h-3.5 w-3.5 mr-1.5"/>Edit
                        </Button>
                        <Button data-testid={`timeline-pay-delete-${ev.payment_id}`} size="sm" variant="outline"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={()=>openPayDelete(ev)}>
                          <Trash2 className="h-3.5 w-3.5 mr-1.5"/>Delete
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {data.events.length === 0 && (
          <div className="pl-8 py-6 text-sm text-muted-foreground">No activity yet.</div>
        )}
      </div>

      {/* ---------- Edit payment (from timeline) ---------- */}
      <Dialog open={payEditOpen} onOpenChange={(v)=>{ if (!payEditBusy) setPayEditOpen(v); }}>
        <DialogContent data-testid="timeline-pay-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit payment · {payEditFor?.invoice_no}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div className="text-xs text-muted-foreground">
              Amount cannot be changed — to fix a wrong amount, delete and re-record.
            </div>
            <div>
              <Label>Paid on</Label>
              <Input data-testid="timeline-pay-edit-date" type="date" value={payEditForm.paid_at}
                max={new Date().toISOString().slice(0,10)}
                onChange={(e)=>setPayEditForm({...payEditForm, paid_at: e.target.value})}/>
            </div>
            <div>
              <Label>Method</Label>
              <Select value={payEditForm.method} onValueChange={(v)=>setPayEditForm({...payEditForm, method: v})}>
                <SelectTrigger data-testid="timeline-pay-edit-method"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                  <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  <SelectItem value="upi">UPI</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference no.</Label>
              <Input data-testid="timeline-pay-edit-ref" value={payEditForm.reference_no}
                onChange={(e)=>setPayEditForm({...payEditForm, reference_no: e.target.value})}/>
            </div>
            <div>
              <Label>Notes</Label>
              <Input data-testid="timeline-pay-edit-notes" value={payEditForm.notes}
                onChange={(e)=>setPayEditForm({...payEditForm, notes: e.target.value})}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPayEditOpen(false)} disabled={payEditBusy}>Cancel</Button>
            <Button data-testid="timeline-pay-edit-submit" onClick={submitPayEdit} disabled={payEditBusy}>
              {payEditBusy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Delete payment (from timeline) ---------- */}
      <Dialog open={payDelOpen} onOpenChange={(v)=>{ if (!payDelBusy) setPayDelOpen(v); }}>
        <DialogContent data-testid="timeline-pay-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete this payment?</DialogTitle>
          </DialogHeader>
          <div className="grid gap-2 py-2 text-sm">
            <div className="text-muted-foreground">
              Removes the payment and restores the invoice's outstanding balance.
            </div>
            <div className="flex justify-between pt-2 border-t border-border mt-1"><span className="text-muted-foreground">Invoice</span><span className="tabular font-medium">{payDelFor?.invoice_no}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Paid on</span><span className="tabular">{fmtDate(payDelFor?.paid_at || payDelFor?.at)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="tabular font-medium text-primary">{fmtINR(Math.abs(payDelFor?.amount || 0))}</span></div>
            {Number(payDelFor?.overpayment || 0) > 0 && (
              <>
                <div className="flex justify-between pt-2 border-t border-border mt-1">
                  <span className="text-muted-foreground">Overpayment credited</span>
                  <span className="tabular font-medium text-amber-700">{fmtINR(payDelFor?.overpayment || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Customer's current credit</span>
                  <span className="tabular font-medium">{fmtINR(data?.summary?.customer_credit_balance || 0)}</span>
                </div>
                {Number(data?.summary?.customer_credit_balance || 0) < Number(payDelFor?.overpayment || 0) ? (
                  <div className="mt-1 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Reversing this overpayment will push the customer's credit to
                    <strong className="mx-1 tabular">{fmtINR((data?.summary?.customer_credit_balance || 0) - (payDelFor?.overpayment || 0))}</strong>.
                    Some credit was already applied to a later invoice — review their account.
                  </div>
                ) : (
                  <div className="mt-1 rounded border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                    After deletion the customer's credit will drop to
                    <strong className="mx-1 tabular text-foreground">{fmtINR((data?.summary?.customer_credit_balance || 0) - (payDelFor?.overpayment || 0))}</strong>.
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPayDelOpen(false)} disabled={payDelBusy}>Cancel</Button>
            <Button data-testid="timeline-pay-delete-confirm" variant="destructive" onClick={submitPayDelete} disabled={payDelBusy}>
              {payDelBusy ? "Deleting…" : "Delete payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
